import { Button, CircularProgress } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const OtpVerificationCard = ({
  className,
  email,
  mobile,
  sourceType,
  otp,
  setOtp,
  handleVerifyOtp,
  handleResendOtp,
  error,
  isLoginFlow,
  isForgotFlow,
  isOnboardFlow,
  resendDisabled,
  timeRemaining,
  setOtpVerificationStep,
  loading,
}) => {
  const otpContainerRef = useRef(null);
  const [otpIptDimension, setOtpIptDimension] = useState({
    width: 0,
    height: 0,
  });
  const OTP_LENGTH = 6;
  const navigate = useNavigate();
  const inputs = useRef([]);
  const handleChange = () => {
    if (isLoginFlow) {
      navigate('/login');
    } else if (isForgotFlow) {
      navigate('/forgot-password');
    } else if (isOnboardFlow) {
      setOtpVerificationStep(1);
    } else {
      navigate('/signup');
    }
  };

  // Helper function to get flow-specific heading
  const getHeading = () => {
    return `6 digit OTP sent to your ${
      sourceType == 'MOBILE' ? 'mobile' : 'email'
    }`; // signup flow
  };

  // Helper function to get flow-specific subtext
  const getSubtext = () => {
    return (
      <>
        Enter OTP sent on <b>{sourceType == 'MOBILE' ? mobile : email}</b>
      </>
    ); // signup flow
  };

  // Helper function to get flow-specific button text
  const getButtonText = () => {
    if (isLoginFlow) return 'Verify & Login';
    if (isForgotFlow) return 'Verify & Continue';
    return 'Verify & Continue'; // signup flow
  };

  const processMultiDigitInput = (inputValue, startIndex) => {
    if (!/^\d*$/.test(inputValue)) {
      return false;
    }

    const digits = inputValue.slice(0, OTP_LENGTH).split('');

    const newOtp = [...otp];
    const remainingFields = OTP_LENGTH - startIndex;

    for (let i = 0; i < digits.length && i < remainingFields; i++) {
      newOtp[startIndex + i] = digits[i];
    }

    setOtp(newOtp);

    const nextFocusIndex = Math.min(startIndex + digits.length, OTP_LENGTH - 1);
    if (inputs.current[nextFocusIndex]) {
      inputs.current[nextFocusIndex].focus();
    }

    return true;
  };

  const handleOtpChange = (e, index) => {
    const { value } = e.target;
    console.log('otpval', value);

    // Handle multi-digit input (paste or autocomplete)
    if (value.length > 1) {
      processMultiDigitInput(value, index);
      return;
    }

    // Handle single digit input
    if (value.match(/^\d$/)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Move focus to the next input
      if (index < OTP_LENGTH - 1) {
        inputs.current[index + 1].focus();
      }
    }

    // Move focus to previous input on backspace
    if (value === '' && index > 0) {
      inputs.current[index - 1].focus();
    }
  };

  const handlePaste = (e, index) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    processMultiDigitInput(pastedData, index);
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace') {
      const newOtp = [...otp];

      // If the current input is empty, move to the previous input
      if (otp[index] === '') {
        if (index > 0) {
          newOtp[index - 1] = ''; // Clear the previous input
          setOtp(newOtp);
          inputs.current[index - 1].focus();
        }
      } else {
        // Clear the current input value
        newOtp[index] = '';
        setOtp(newOtp);
      }
    }
  };
  useEffect(() => {
    const updateWidth = () => {
      if (otpContainerRef.current) {
        let width = otpContainerRef.current.clientWidth / 6 - 16;
        let height = width * 1.43;
        setOtpIptDimension({ width: width, height: height });
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => {
      window.removeEventListener('resize', updateWidth);
    };
  }, [otpContainerRef]);
  return (
    <Wrapper className={`card-wrapper ${className}`}>
      <div className="card-wrapper-box">
        <h4>{getHeading()}</h4>
        <div className="subtext-container">
          <p className="sub-text">{getSubtext()}</p>
          {sourceType != 'MOBILE' && (
            <button onClick={handleChange} className="change-btn">
              Change
            </button>
          )}
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleVerifyOtp(sourceType);
          }}
        >
          <div className="otp-container" ref={otpContainerRef}>
            {otp.map((_, index) => (
              <input
                key={index}
                type="text"
                // maxLength="1"
                value={otp[index]}
                onChange={(e) => handleOtpChange(e, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                onPaste={(e) => handlePaste(e, index)}
                ref={(el) => (inputs.current[index] = el)}
                inputMode="numeric"
                style={{
                  width: otpIptDimension.width,
                  height: otpIptDimension.height,
                }}
              />
            ))}
          </div>
          {isForgotFlow && (
            <p className="forgot-flow-message">
             *An OTP will be sent to your email if it is linked to an existing account.
            </p>
          )}
          <Button
            fullWidth
            variant="contained"
            type="submit"
            disabled={otp.join('').length !== OTP_LENGTH || loading}
          >
            {loading ? (
              <CircularProgress style={{ color: '#fff' }} size={26} />
            ) : (
              getButtonText()
            )}
          </Button>
        </form>

        <div className="resend-section">
          {resendDisabled ? (
            timeRemaining > 0 ? (
              <p className="timer">
                Resend code in {Math.floor(timeRemaining / 60)}:
                {(timeRemaining % 60).toString().padStart(2, '0')}
              </p>
            ) : timeRemaining ? (
              <p className="max-resend">Maximum resend attempts reached</p>
            ) : (
              ''
            )
          ) : (
            <button onClick={handleResendOtp} className="resend-btn">
              Resend verification code
            </button>
          )}
        </div>
      </div>

      {!isOnboardFlow && (
        // Show different bottom links based on flow
        <div className="sign-up-link">
          {isLoginFlow && (
            <p>
              Don't have an account? <Link to="/signup">Sign up</Link>
            </p>
          )}
          {isForgotFlow && (
            <p>
              Remember your password? <Link to="/login">Login</Link>
            </p>
          )}
          {!isLoginFlow && !isForgotFlow && (
            <p>
              Already have an account? <Link to="/login">Login</Link>
            </p>
          )}
        </div>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  /* Update OTP input styles */

  .otp-container {
    display: flex;
    gap: 0px;
    justify-content: space-between;
    margin: 32px 0;
    max-width: 400px;
  }

  .otp-container input {
    width: 48px;
    height: 56px;
    border: 1px solid #374151;
    border-radius: 8px;
    font-size: 20px;
    font-weight: 500;
    text-align: center;
    color: #1a1f36;
    background: #ffffff;
    outline: none;
    transition: all 0.2s ease;
  }

  .otp-container input:focus {
    border-color: #556cd6;
    box-shadow: 0 0 0 2px rgba(85, 108, 214, 0.1);
  }

  .otp-container input::-webkit-outer-spin-button,
  .otp-container input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  .otp-container input[type='number'] {
    -moz-appearance: textfield;
  }
    
  .forgot-flow-message {
    font-style: italic;
    color: #6b7280;
    font-size: 13px !important;
    margin: 16px 0 24px 0;
    // text-align: center;
  }
  // @media all and (max-width: 768px) {
  //   .otp-container {
  //     max-width: 400px;
  //   }
  // }
`;
export default OtpVerificationCard;
